import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  getShoppingList,
  removeShoppingItems,
  SHOPPING_LIST_CHANGED_EVENT,
  ShoppingItem,
  ShoppingListResponse,
} from "../services/shoppingService";
import { PanelHeader, PanelState } from "./Panel";

// How long a ticked item stays crossed out before it's removed, so a stray
// tap can be undone.
const TICK_OFF_DELAY_MS = 4000;

export const ShoppingList = () => {
  const [items, setItems] = useState<ShoppingItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [ticked, setTicked] = useState<Set<string>>(new Set());
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  const refresh = useCallback(async () => {
    try {
      const result = await getShoppingList();
      setItems(result.items);
      setError(null);
    } catch (e: any) {
      setError(e?.message || "Failed to load shopping list");
      console.error("Failed to fetch shopping list:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    const interval = setInterval(refresh, 60 * 1000);

    const onChanged = (e: Event) => {
      setItems((e as CustomEvent<ShoppingListResponse>).detail.items);
    };
    window.addEventListener(SHOPPING_LIST_CHANGED_EVENT, onChanged);

    const pending = timers.current;
    return () => {
      clearInterval(interval);
      window.removeEventListener(SHOPPING_LIST_CHANGED_EVENT, onChanged);
      pending.forEach(clearTimeout);
    };
  }, [refresh]);

  const untick = (id: string) => {
    clearTimeout(timers.current.get(id));
    timers.current.delete(id);
    setTicked((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  const toggle = (id: string) => {
    if (ticked.has(id)) {
      untick(id);
      return;
    }

    setTicked((prev) => new Set(prev).add(id));
    timers.current.set(
      id,
      setTimeout(async () => {
        try {
          await removeShoppingItems([id]);
        } catch (e) {
          console.error("Failed to remove shopping item:", e);
          refresh();
        }
        untick(id);
      }, TICK_OFF_DELAY_MS),
    );
  };

  let body: React.ReactNode;
  if (loading && items.length === 0) {
    body = <PanelState>Loading…</PanelState>;
  } else if (error) {
    body = <PanelState tone="error">{error}</PanelState>;
  } else if (items.length === 0) {
    body = <PanelState>List is empty</PanelState>;
  } else {
    body = (
      <ol className="hud-list shopping-list__items">
        {items.map((item, i) => (
          <li key={item.id}>
            <button
              type="button"
              className={`shopping-list__item ${
                ticked.has(item.id) ? "is-ticked" : ""
              }`}
              onClick={() => toggle(item.id)}
              aria-pressed={ticked.has(item.id)}
            >
              <span className="shopping-list__index">
                {String(i + 1).padStart(2, "0")}
              </span>
              <span className="shopping-list__text">{item.text}</span>
            </button>
          </li>
        ))}
      </ol>
    );
  }

  const remaining = items.length - ticked.size;

  return (
    <div className="shopping-list">
      <PanelHeader label="Shopping" meta={items.length ? "Tap to tick off" : null} />
      <div className="shopping-list__count">
        <span className="shopping-list__num">
          {String(error ? 0 : remaining).padStart(2, "0")}
        </span>
        <span className="shopping-list__unit">
          {remaining === 1 ? "Item" : "Items"}
        </span>
      </div>
      {body}
    </div>
  );
};
